import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ButtonComponent } from '../../button/button.component';
import { CloseComponent } from '../../close/close.component';

export interface ModalAction {
  label: string;
  loading?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

@Component({
  selector: 'ui-modal-full',
  templateUrl: './modal-full.component.html',
  styleUrl: './modal-full.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, ButtonComponent, CloseComponent],
})
export class ModalFullComponent implements OnInit {
  @HostBinding('attr.title') hostTitle = null; // Explicitly prevent title attribute on host element to prevent tooltips showing due to `title` overlap with HTML title - we should rename this soon

  @Input({ required: true }) title!: string;
  @Input() size: 'sm' | 'md' = 'md';
  @Input() primaryAction: ModalAction | null = null;
  @Input() secondaryAction: ModalAction | null = null;
  @Input() disableContainerChange = false;
  @Input() centerContent = false;
  @Input() hideFooter = false;
  @Input() hideHeader = false;
  @Input() progress = 0;
  @Output() backClicked = new EventEmitter<void>();
  @Output() closeClicked = new EventEmitter<void>();

  get sizeClass() {
    if (this.size === 'sm') {
      return 'max-w-[520px]';
    }

    return 'max-w-[640px]';
  }

  get contentClass() {
    return this.centerContent ? 'grid items-center' : '';
  }

  get actionsIndent() {
    if (this.primaryAction && this.secondaryAction) {
      return 'justify-between';
    }

    if (this.primaryAction && !this.secondaryAction) {
      return 'justify-end';
    }

    return 'justify-start';
  }

  get contentTopMargin() {
    if (this.hideHeader) {
      return 'my-32';
    }

    return this.progress > 0 ? 'mt-36' : 'my-32';
  }

  constructor(private elementRef: ElementRef) {}

  ngOnInit(): void {
    this.prepareContainer();
  }

  private prepareContainer(): void {
    if (this.disableContainerChange) return;

    let element = this.elementRef.nativeElement as HTMLElement;
    let inheritanceLevel = 0;

    while (element.parentElement && inheritanceLevel < 10) {
      element = element.parentElement;
      inheritanceLevel++;

      if (element.classList.contains('cdk-overlay-pane')) {
        element.style.width = '100%';
        element.style.height = '100%';
        element.style.maxWidth = '100%';
        element.style.maxHeight = '100%';
        element.classList.add('dialog__no-padding');
        break;
      }
    }
  }
}
